import React, { useRef } from 'react';
import BookprocessWidgetProps from './widgets/interfaces/BookprocessWidgetPropsInterface';
import { BookprocessRecap, RecapItems } from './BookprocessRecap';
import { LoadingIndicator } from './LoadingIndicator';
import { trackPromise } from 'react-promise-tracker';
import BookprocessWidget from './BookprocessWidgets';
import { createPurchasePayload, sendTagManagerEvent } from './gtagEvent';
import { bookingInfo } from './bookingInfoInterface';

interface Link {
    href: string;
    method?: 'GET' | 'POST' | 'PUT';
}
interface LinkWithText extends Link {
    text: string;
}

const fetchLink = (l: Link, init: RequestInit): Promise<Response> =>
    trackPromise(fetch(l.href, Object.assign({ method: l.method || 'GET' }, init)));

interface bpData {
    [propName: string]: string;
}

const LINK_RELATION_MAKE_RESERVATION = 'recras:bookprocess:make_reservation';
const LINK_RELATION_ONLINE_PAYMENT = 'recras:bookprocess:make_reservation_online_payment';
const LINK_RELATION_VALIDATE = 'recras:bookprocess:validate';
const LINK_RELATION_PAYMENT = 'payment';
const LINK_RELATION_CHECK_TERMINAL_PAYMENT = 'recras:bookprocess:checkterminalpayment';
const LINK_RELATION_SUCCESS = 'recras:bookprocess:success';
const LINK_RELATION_CONFIRMATION = 'recras:bookprocess:confirmation';

interface BookprocessStepResponse {
    form: [BookprocessWidgetProps];
    _links: {
        [propName: string]: LinkWithText;
    };
    translations: {
        [key: string]: string;
    };
    recap: {
        items: RecapItems;
        title: string;
    };
    info: bookingInfo;
}

interface ColourTheme {
    base: string;
    middle: string;
    light: string;
    text: string;
    text_alt: string;
    background: string;
}

interface BookprocessStepState {
    showSteps: boolean;
    step: BookprocessStepResponse;
    stepCount: number;
    stepCurrent: number;
    data: bpData;
    showAllErrors: boolean;
    dialogText: string;
}

interface BookprocessParameter {
    selfURL: URL;
    firstWidgetValue?: string;
}

interface BookprocessError {
    http_error: number;
    message: string;
}

export class BookprocessStep extends React.Component<BookprocessParameter, BookprocessStepState> {
    formStack: BookprocessStepResponse[] = [];
    private formRef = React.createRef<HTMLFormElement>();
    private dialogRef = React.createRef<HTMLDialogElement>();
    private prevUrlData: string = null;
    private prevController: AbortController = null;
    private submitting: boolean = false;
    private theme: ColourTheme;
    private printConfirmation: boolean;

    constructor(props: BookprocessParameter) {
        super(props);
        this.state = {
            showSteps: false,
            step: undefined,
            stepCount: undefined,
            stepCurrent: undefined,
            data: { firstWidgetValue: props.firstWidgetValue },
            showAllErrors: false,
            dialogText: '',
        };
        this.widgetChanged = this.widgetChanged.bind(this);
        this.getStateValue = this.getStateValue.bind(this);
        this.printConfirmation = new URLSearchParams(window.location.search).has(
            'printConfirmation'
        );
    }

    componentDidMount() {
        fetchLink({ href: this.props.selfURL.toString() }, {})
            .then(resp => resp.json())
            .then(bp => {
                this.setState({
                    showSteps: bp._extra.show_steps,
                    stepCount: bp._extra.steps,
                });
                if (bp._colour_theme) {
                    this.theme = bp._colour_theme;
                }
                this.fetchAndSetStep(bp._links.start, new URLSearchParams());
            });
    }

    fetchStepErrorHandler(error: Error) {
        this.submitting = false;
        if (error.name === 'AbortError') {
            // aborted because of new request
        } else {
            throw error;
        }
    }

    fetchStep(link: Link, data: URLSearchParams): Promise<BookprocessStepResponse> {
        let contentType: string;
        const controller = new AbortController();
        const signal = controller.signal;

        if (this.prevController) {
            this.prevController.abort();
        }
        this.prevController = controller;

        return fetchLink(link, { body: data, signal }).then(resp => {
            if (!resp.ok) {
                resp.json().then(errs => {
                    const msgs = errs.map((err: BookprocessError) => err.message);
                    alert(msgs.join('\n'));
                });
                throw new Error('Status code outside of 200-299 range');
            }
            contentType = resp.headers.get('Content-type');
            if (contentType.startsWith('application/hal+json')) {
                return resp.json();
            }
            throw new Error(
                'BookprocessStep.fetchStep can only handle application/hal+json, got ' + contentType
            );
        });
    }

    sendHeightToParent() {
        // only required in iFrame, so using the bookprocess id instead of class
        const bookprocessEle = document.getElementById('bookprocess');
        if (!bookprocessEle) {
            return;
        }
        const contentHeight = bookprocessEle.scrollHeight;
        window.parent.postMessage(
            {
                iframeHeight: contentHeight,
                url: window.location.href,
            },
            '*'
        );
    }

    formatGA4Items(info: bookingInfo) {
        let fmt = [];
        for (let item of info.items) {
            fmt.push({
                item_id: item.itemId,
                item_name: item.itemName,
                price: item.price,
                quantity: item.quantity,
            });
        }
        return fmt;
    }

    sumOfItemPrices(info: bookingInfo) {
        let sum = 0;
        for (let item of info.items) {
            sum += item.price * item.quantity;
        }
        return sum;
    }

    fetchAndSetStep(link: Link, data: URLSearchParams) {
        return this.fetchStep(link, data)
            .then(bp => {
                if ((this.state || {}).step) {
                    this.formStack.push(this.state.step);
                }
                this.setState(
                    {
                        step: bp,
                        stepCurrent: bp.info.step,
                        showAllErrors: false,
                    },
                    () => {
                        let items = this.formatGA4Items(bp.info);
                        if (items.length) {
                            sendTagManagerEvent('add_to_cart', {
                                currency: bp.info.currency,
                                value: this.sumOfItemPrices(bp.info),
                                items: items,
                            });
                        }
                        sendTagManagerEvent('select_content', {
                            content_type: 'bookprocess_step',
                            item_id:
                                'bookprocess' +
                                this.state.step.info.bookprocessId +
                                '_step' +
                                this.state.step.info.step,
                        });
                        this.sendHeightToParent();
                        this.formRef.current.scrollIntoView({
                            behavior: 'smooth',
                        });
                    }
                );
                return bp;
            })
            .catch(this.fetchStepErrorHandler.bind(this));
    }

    createBackButton(text: string) {
        return React.createElement(
            'button',
            {
                type: 'button',
                className: 'previousStep',
                onClick: event => {
                    event.preventDefault();
                    let popped = this.formStack.pop();
                    this.setState(
                        {
                            step: popped,
                            stepCurrent: this.state.step.info.step - 1,
                            showAllErrors: false,
                        },
                        () => {
                            let items = this.formatGA4Items(this.state.step.info);
                            if (items.length) {
                                sendTagManagerEvent('remove_from_cart', {
                                    currency: this.state.step.info.currency,
                                    value: this.sumOfItemPrices(this.state.step.info),
                                    items: items,
                                });
                            }
                            sendTagManagerEvent('select_content', {
                                content_type: 'bookprocess_step',
                                item_id:
                                    'bookprocess' +
                                    this.state.step.info.bookprocessId +
                                    '_step' +
                                    this.state.step.info.step,
                            });
                            this.sendHeightToParent();
                        }
                    );
                },
            },
            text
        );
    }

    createNextButton(text: string) {
        return React.createElement(
            'button',
            { className: 'primary nextStep', onClick: this.submitNext.bind(this) },
            text
        );
    }

    createFinaliseButton(text: string) {
        return React.createElement(
            'button',
            { className: 'primary', onClick: this.submitFinalise.bind(this) },
            text
        );
    }

    createPaymentButton(text: string) {
        return React.createElement(
            'button',
            { className: 'primary', onClick: this.startPayment.bind(this) },
            text
        );
    }

    createPaymentTerminalDialog() {
        return React.createElement('dialog', { ref: this.dialogRef }, this.state.dialogText);
    }

    trustedHtml(val: any) {
        return { __html: val };
    }

    isValidFirstValue(val: string, recrasType: string) {
        let matches;
        switch (recrasType) {
            case 'package':
                matches = val.match(/^[0-9]+$/);
                if (matches === null) {
                    return false;
                }
                const parsed = parseInt(val, 10);
                return !isNaN(parsed) && parsed > 0;
            default:
                return false;
        }
    }

    createStepsHtml() {
        let stepsEle = [];
        for (let i = 1; i <= this.state.stepCount; i++) {
            stepsEle.push(
                React.createElement(
                    'li',
                    {
                        className: i === this.state.stepCurrent ? 'current' : undefined,
                    },
                    i
                )
            );
        }
        return React.createElement(
            'ol',
            {
                className: 'steps',
            },
            stepsEle
        );
    }

    render() {
        if (!this.state.step) {
            return null;
        }
        let stepsEle = null;
        if (this.state.showSteps) {
            stepsEle = this.createStepsHtml();
        }
        let widgets = this.state.step.form.map((widget, idx) => {
            widget.key = widget.id;
            widget.handleChange = (name: string, value: string, validate: boolean = true) =>
                this.widgetChanged(name, value, validate);
            if (!widget.classNames) {
                widget.classNames = [];
            }
            if (idx === 0 && this.state.data.firstWidgetValue) {
                if (this.isValidFirstValue(this.state.data.firstWidgetValue, widget.recrastype)) {
                    widget.value = this.state.data.firstWidgetValue;
                    widget.classNames.push('has-initial-value');
                }
            }
            if (!widget.value && widget.name in this.state.data) {
                widget.value = this.state.data[widget.name];
            }
            widget.showError = this.state.showAllErrors;

            widget.getStateValue = (name: string) => this.getStateValue(name);
            widget.dataAsUrlSearchParams = () => this.dataAsUrlSearchParams();
            return React.createElement(BookprocessWidget, widget);
        });

        let backButton;
        if (this.state.step._links.previous) {
            backButton = this.createBackButton(this.state.step._links.previous.text);
        }

        let button;
        if (this.state.step._links.next) {
            button = this.createNextButton(this.state.step._links.next.text);
        } else if (this.state.step._links[LINK_RELATION_MAKE_RESERVATION]) {
            button = this.createFinaliseButton(
                this.state.step._links[LINK_RELATION_MAKE_RESERVATION].text
            );
        } else if (this.state.step._links[LINK_RELATION_ONLINE_PAYMENT]) {
            button = this.createPaymentButton(
                this.state.step._links[LINK_RELATION_ONLINE_PAYMENT].text
            );
        }

        let loadingIndicatorEl = React.createElement(LoadingIndicator);
        let buttonWrapperEl = React.createElement(
            'div',
            { className: 'bpButtonWrapper' },
            backButton,
            button
        );
        let fs = React.createElement('fieldset', {}, widgets, loadingIndicatorEl);

        let recap;

        if (this.state.step.recap) {
            recap = React.createElement(BookprocessRecap, this.state.step.recap);
        }

        const form = React.createElement(
            'form',
            {
                className: 'bpMainForm',
                ref: this.formRef,
                onSubmit: event => {
                    event.preventDefault();
                    this.submitNext(event);
                },
            },
            stepsEle,
            fs
        );

        let props: any = {
            style: undefined,
        };
        if (this.theme) {
            props.style = {
                '--bp-col-dark': this.theme.base,
                '--bp-col-middle': this.theme.middle,
                '--bp-col-light': this.theme.light,
                '--bp-color-text': this.theme.text,
                '--bp-color-text-alt': this.theme.text_alt,
                '--bp-color-background': this.theme.background,
            };
            props.className = 'version2'; // Backwards compatibility
        }

        let dialog = this.createPaymentTerminalDialog();

        return React.createElement('div', props, form, buttonWrapperEl, recap, dialog);
    }

    printPDF(pdfUrl: string, callback: Function = null): void {
        const iframe = document.createElement('iframe');
        iframe.id = 'invoicePdfIframe';
        iframe.src = pdfUrl;
        iframe.style.display = 'none';
        iframe.onload = () => {
            const content = iframe.contentWindow;
            content.focus();
            try {
                content.print();
                if (callback) {
                    callback();
                }
            } catch (e) {
                window.open(pdfUrl, 'invoicePdf');
            }
        };
        document.querySelector('body').appendChild(iframe);
    }

    checkTerminalPayment(checkUrl: URL): void {
        fetch(checkUrl)
            .then(resp => resp.json())
            .then(result => {
                // check result.status
                if (result.status === 'open') {
                    window.setTimeout(() => {
                        this.checkTerminalPayment(checkUrl);
                    }, 1000);
                } else if (result.status === 'success') {
                    if (
                        LINK_RELATION_CONFIRMATION in (result._links || {}) &&
                        this.printConfirmation
                    ) {
                        this.printPDF(result._links[LINK_RELATION_CONFIRMATION].href);
                    }
                    this.setState({
                        dialogText: result.message,
                    });
                    this.submitting = false;
                    window.setTimeout(() => {
                        window.location.reload();
                    }, 8000);
                } else if (result.status === 'cancelled') {
                    this.setState({
                        dialogText: result.message,
                    });
                    this.submitting = false;
                    window.setTimeout(() => {
                        this.dialogRef.current.close();
                    }, 5000);
                }
            });
    }

    submitAndContinue(link: Link, data: URLSearchParams) {
        if (this.submitting) {
            return;
        }
        this.submitting = true;
        this.fetchStep(link, data)
            .then((bp: BookprocessStepResponse) => {
                if (LINK_RELATION_PAYMENT in (bp._links || {})) {
                    sendTagManagerEvent('begin_checkout', {
                        currency: bp.info.currency,
                        value: bp.info.transactionValue,
                    }).then(() => {
                        window.top.location.href = new URL(
                            bp._links[LINK_RELATION_PAYMENT].href,
                            window.location.href
                        ).toString();
                    });
                } else if (
                    LINK_RELATION_CONFIRMATION in (bp._links || {}) &&
                    this.printConfirmation
                ) {
                    this.printPDF(bp._links[LINK_RELATION_CONFIRMATION].href, function () {
                        if (LINK_RELATION_SUCCESS in (bp._links || {})) {
                            window.setTimeout(() => {
                                window.top.location.href = new URL(
                                    bp._links[LINK_RELATION_SUCCESS].href,
                                    window.location.href
                                ).toString();
                            }, 2000);
                        }
                    });
                } else if (LINK_RELATION_SUCCESS in (bp._links || {})) {
                    window.top.location.href = new URL(
                        bp._links[LINK_RELATION_SUCCESS].href,
                        window.location.href
                    ).toString();
                } else if (LINK_RELATION_CHECK_TERMINAL_PAYMENT in (bp._links || {})) {
                    this.dialogRef.current.showModal();
                    let checkUrl = new URL(
                        bp._links[LINK_RELATION_CHECK_TERMINAL_PAYMENT].href,
                        window.location.href
                    );
                    this.setState({
                        dialogText: bp._links[LINK_RELATION_CHECK_TERMINAL_PAYMENT].text,
                    });
                    this.checkTerminalPayment(checkUrl);
                } else {
                    this.setState(state => {
                        state.step.form = bp.form;
                        return state;
                    });
                }
            })
            .catch(this.fetchStepErrorHandler.bind(this));
    }

    hasAnyError(): boolean {
        for (let widget of this.state.step.form) {
            if (widget.error) {
                return true;
            }
        }
        return false;
    }

    async submitFinalise(event: React.FormEvent<HTMLFormElement>) {
        await this.validateStep();
        this.setState({ showAllErrors: true });
        if (!this.formRef.current.checkValidity()) {
            this.scrollToFirstError();
            return false;
        }
        event.preventDefault();
        if (this.hasAnyError()) {
            this.scrollToFirstError();
            return false;
        }
        let link = this.state.step._links[LINK_RELATION_MAKE_RESERVATION];
        let data = new URLSearchParams(postData(this.state.data, fieldnames(this.state.step.form)));
        this.submitAndContinue(link, data);
        return false;
    }

    async startPayment(event: React.FormEvent<HTMLFormElement>) {
        await this.validateStep();
        this.setState({ showAllErrors: true });
        if (!this.formRef.current.checkValidity()) {
            this.scrollToFirstError();
            return false;
        }
        event.preventDefault();
        if (this.hasAnyError()) {
            this.scrollToFirstError();
            return false;
        }
        let link = this.state.step._links[LINK_RELATION_ONLINE_PAYMENT];
        let data = new URLSearchParams(postData(this.state.data, fieldnames(this.state.step.form)));
        this.submitAndContinue(link, data);
        return false;
    }

    scrollToFirstError() {
        const timeoutFn = function (form: HTMLFormElement) {
            const firstErrorEl = form.querySelector('.error');
            if (!firstErrorEl) {
                return;
            }
            firstErrorEl.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            });
        };
        window.setTimeout(() => {
            timeoutFn(this.formRef.current);
        }, 100);
    }

    async submitNext(event: React.FormEvent<HTMLFormElement>) {
        await this.validateStep();
        this.setState({ showAllErrors: true });
        if (!this.formRef.current.checkValidity()) {
            this.scrollToFirstError();
            return false;
        }
        event.preventDefault();
        if (this.hasAnyError()) {
            this.scrollToFirstError();
            return false;
        }
        let link = this.state.step._links.next;
        let data = new URLSearchParams(postData(this.state.data, fieldnames(this.state.step.form)));
        return this.fetchAndSetStep(link, data);
    }

    validateStep() {
        let link = this.state.step._links[LINK_RELATION_VALIDATE];

        let data = new URLSearchParams(postData(this.state.data, fieldnames(this.state.step.form)));
        if (data.toString() === this.prevUrlData) {
            return Promise.resolve();
        }
        this.prevUrlData = data.toString();

        return this.fetchStep(link, data)
            .then(bp => {
                if (bp.form) {
                    return new Promise<void>(resolve => {
                        this.setState({ step: bp }, () => {
                            this.sendHeightToParent();
                            resolve();
                        });
                    });
                }
            })
            .catch(this.fetchStepErrorHandler.bind(this));
    }

    widgetChanged(name: string, value: string, validate: boolean = true) {
        if (!name) {
            return;
        }

        let widgets = this.state.step.form.map(widget => {
            if (widget.depends_on === name) {
                // There is another widget that depends on this one. Clear its value
                this.widgetChanged(widget.id, null);
                let subInputs = fieldnames(widget.inputs);
                for (let subInput of subInputs) {
                    this.widgetChanged(subInput, null);
                }
            }
        });

        this.setState(state => {
            if (value === null) {
                delete state.data[name];
            } else {
                state.data[name] = value;
            }
            if (validate) {
                this.validateStep();
            }
            return state;
        });
    }

    dataAsUrlSearchParams(): URLSearchParams {
        let data = new URLSearchParams(postData(this.state.data, fieldnames(this.state.step.form)));
        return data;
    }

    getStateValue(name: string): string {
        if (name in this.state.data) {
            return this.state.data[name];
        }
        return null;
    }
}

interface namedinput {
    name?: string;
    inputs?: namedinput[];
}
function fieldnames(form: namedinput[]): string[] {
    let names: string[] = [];
    for (let f of form) {
        if (f.name) {
            names.push(f.name);
        }
        if (f.inputs) {
            names = names.concat(fieldnames(f.inputs));
        }
    }
    return names;
}

function postData(data: Readonly<{ [key: string]: string }>, names: Readonly<string[]>) {
    let filteredData: { [key: string]: string } = {};
    for (let [k, v] of Object.entries(data)) {
        if (names.includes(k) && v !== '') {
            filteredData[k] = v;
        }
    }
    return filteredData;
}
